import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

import api from '../../utils/api';
import ChatWindow from '../chat/ChatWindow';
import { useCall } from '../../context/CallContext';
import '../../styles/messenger.css';

/**
 * Messages while on a call, in the window's right-hand dock — Meet's chat panel.
 *
 * Not a chat of its own. The server decides which conversation a call types
 * into (utils/callSignalling.js → syncCallChat): the pair's direct chat while
 * it is two people, then a "Call at …" group made for the call once a third
 * joins. This shows that conversation with the ordinary ChatWindow, so a
 * message sent here is the same message in the Chats page, with the same
 * replies, mentions, attachments and ticks — and still there after the call.
 */
const CallChatPanel = ({ onClose }) => {
    const { call } = useCall();
    const conversationId = call.chatConversationId;

    const [conversation, setConversation] = useState(null);
    const [failed, setFailed] = useState(false);

    const me = (() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}') || {}; } catch { return {}; }
    })();
    const myId = me._id || me.id;

    useEffect(() => {
        if (!conversationId) { setConversation(null); return undefined; }

        let cancelled = false;
        setFailed(false);
        api.get(`/conversations/${conversationId}`)
            .then((res) => { if (!cancelled) setConversation(res.data); })
            .catch(() => { if (!cancelled) setFailed(true); });
        return () => { cancelled = true; };
    }, [conversationId]);

    const ready = conversation && String(conversation._id) === String(conversationId);

    return (
        <aside className="call-panel call-chat-panel" aria-label="In-call messages">
            <header className="call-panel-head">
                <h3>
                    In-call messages
                    {ready && conversation.kind === 'group' && (
                        <span className="call-chat-sub">{conversation.title}</span>
                    )}
                </h3>
                <button
                    type="button"
                    className="call-window-bar-btn call-panel-close"
                    onClick={onClose}
                    title="Close"
                    aria-label="Close messages"
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            </header>

            {!conversationId && (
                <p className="call-panel-empty">Messages open once somebody joins the call.</p>
            )}
            {conversationId && failed && (
                <p className="call-panel-empty">Could not open the messages for this call.</p>
            )}
            {conversationId && !failed && !ready && (
                <p className="call-panel-empty">Loading…</p>
            )}

            {ready && (
                <div className="call-chat-body">
                    <ChatWindow
                        key={conversation._id}
                        embedded
                        conversation={conversation}
                        myId={myId}
                        myRole={me.role}
                    />
                </div>
            )}
        </aside>
    );
};

export default CallChatPanel;
